/**
 * ====================================================================
 * CONFIGURATION UNIFIÉE PEELOCHAT DASHBOARD
 * ====================================================================
 * 
 * Ce fichier centralise toutes les configurations de l'application
 * Évite les conflits entre multiples fichiers de config
 * 
 * Variables d'environnement supportées :
 * - VUE_APP_API_URL : URL de l'API principale
 * - VUE_APP_API_BASE_URL : URL de base pour l'API  
 * - VUE_APP_ECOMMERCE_API_URL : URL de l'API e-commerce
 * - VUE_APP_ECOMMERCE_DASHBOARD_URL : URL du dashboard e-commerce
 * - VUE_APP_MAIN_DASHBOARD_URL : URL du dashboard principal
 * - VUE_APP_CRYPTO_SECRET : Clé secrète pour le cryptage
 */

// ====================================================================
// DÉTECTION DE L'ENVIRONNEMENT
// ====================================================================

// Vérification sécurisée de l'environnement (compatible SSR)
const isLocalhost = typeof window !== 'undefined' && 
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

const protocol = isLocalhost ? 'http' : 'https';

// ====================================================================
// CONFIGURATION API PRINCIPALE
// ====================================================================

// Domaines par défaut
const PRODUCTION_API_DOMAIN = 'api.peelo.chat';
const PRODUCTION_ECOMMERCE_API_DOMAIN = 'apicommerce.peelo.chat';

// Configuration de l'API principale
const USE_MOCK_API = false; // Set to true to use mock data instead of real API
const useCorsProxy = isLocalhost; // Utiliser le proxy CORS en développement

// URL de l'API principale - CORRECTION DU PROBLÈME
export const apiBaseUrl = USE_MOCK_API 
  ? '/mock-api' // Mode simulation
  : (useCorsProxy 
    ? '/api' // Développement avec proxy CORS (vue.config.js)
    : `${protocol}://${PRODUCTION_API_DOMAIN}`); // Production avec URL fixe

// ====================================================================
// URLS DES SERVICES
// ====================================================================

// API E-commerce
export const ecommerceApiUrl = process.env.VUE_APP_ECOMMERCE_API_URL || 
  `${protocol}://${PRODUCTION_ECOMMERCE_API_DOMAIN}`;

// Dashboard E-commerce
export const ecommerceDashboardUrl = process.env.VUE_APP_ECOMMERCE_DASHBOARD_URL || 
  (isLocalhost ? 'https://localhost:8081' : 'https://ecommerce.peelo.chat');

// Dashboard principal  
export const mainDashboardUrl = process.env.VUE_APP_MAIN_DASHBOARD_URL ||
  (isLocalhost ? 'https://localhost:8080' : 'https://dashboard.peelo.chat');

// Jarvis API pour l'IA
export const jarvisApiUrl = 'https://jarvis.aipeelo.xyz';

// ====================================================================
// CONFIGURATION CRYPTAGE
// ====================================================================

export const cryptoSecret = process.env.VUE_APP_CRYPTO_SECRET || 
  'peelochat-dashboard-secret-key-2024';

// ====================================================================
// CONFIGURATION AVATAR
// ====================================================================

export const avatarConfig = {
  defaultBackground: 'random',
  defaultColor: 'fff',
  defaultSize: 100,
  baseUrl: apiBaseUrl
};

// ====================================================================
// CONFIGURATION UPLOAD
// ====================================================================

export const uploadConfig = {
  maxFileSize: 10 * 1024 * 1024, // 10MB
  allowedTypes: ['image/jpeg', 'image/png', 'image/gif', 'audio/mpeg', 'audio/wav', 'application/pdf'],
  baseUrl: apiBaseUrl
};

// ====================================================================
// CONFIGURATION WHATSAPP
// ====================================================================

export const whatsappConfig = {
  // Configuration IDs pour les différents types de comptes
  // Peut être surchargé par les variables d'environnement
  ecommerceConfigId: process.env.VUE_APP_WHATSAPP_ECOMMERCE_CONFIG_ID || '715701984540041',
  customerSupportConfigId: process.env.VUE_APP_WHATSAPP_CUSTOMER_SUPPORT_CONFIG_ID || '1593335085387009',
  
  // App ID Facebook pour le SDK
  facebookAppId: process.env.VUE_APP_FACEBOOK_APP_ID || '1668577657360308',
  
  // Timeouts
  verificationCodeTimeout: 10 * 60 * 1000, // 10 minutes
  connectionTimeout: 15 * 1000 // 15 secondes
};

// ====================================================================
// CONFIGURATION DE DEBUG
// ====================================================================

export const debugConfig = {
  enableConsoleLogging: isLocalhost,
  enableAPILogging: isLocalhost,
  showNetworkErrors: true
};

// ====================================================================
// EXPORTS COMPATIBILITÉ (pour éviter les breaking changes)
// ====================================================================

// Export pour compatibilité avec les anciens imports
export default {
  apiBaseUrl,
  ecommerceApiUrl,
  ecommerceDashboardUrl,
  mainDashboardUrl,
  jarvisApiUrl,
  cryptoSecret,
  avatarConfig,
  uploadConfig,
  whatsappConfig,
  debugConfig,
  
  // Informations d'environnement
  isLocalhost,
  protocol,
  USE_MOCK_API,
  useCorsProxy
};

// ====================================================================
// UTILITAIRES
// ====================================================================

/**
 * Construit une URL complète pour une ressource
 * @param {string} path - Le chemin de la ressource
 * @param {string} baseUrl - L'URL de base (optionnel)
 * @returns {string} L'URL complète
 */
export const buildResourceUrl = (path, baseUrl = apiBaseUrl) => {
  if (!path) return baseUrl;
  
  const cleanPath = path.startsWith('/') ? path.slice(1) : path;
  const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
  
  return `${cleanBaseUrl}/${cleanPath}`;
};

/**
 * Vérifie si l'application est en mode développement
 * @returns {boolean}
 */
export const isDevelopment = () => isLocalhost;

/**
 * Vérifie si l'application est en mode production
 * @returns {boolean}
 */
export const isProduction = () => !isLocalhost;

/**
 * Log conditionnel basé sur l'environnement
 * @param {...any} args - Arguments à logger
 */
export const devLog = (...args) => {
  if (debugConfig.enableConsoleLogging) {
    console.log('[DEV]', ...args);
  }
};

/**
 * Log d'erreur avec contexte
 * @param {string} context - Contexte de l'erreur
 * @param {Error} error - L'erreur
 */
export const errorLog = (context, error) => {
  if (debugConfig.showNetworkErrors) {
    console.error(`[ERROR] ${context}:`, error);
  }
}; 